import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { formatApiError } from '@/lib/api';
import { Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function SignupPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success('Account created! Welcome to ContractEye.');
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-6" data-testid="signup-page">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Link to="/" className="inline-flex items-center gap-2 mb-6" data-testid="signup-logo-link">
            <Eye className="w-6 h-6 text-brand" strokeWidth={2} />
            <span className="font-heading font-black text-lg tracking-tight text-zinc-900">ContractEye</span>
          </Link>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-zinc-900">Create your account</h1>
          <p className="font-body text-sm text-zinc-500 mt-1">Start scanning contracts for free</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white border border-zinc-200 p-8" data-testid="signup-form">
          {error && (
            <div className="bg-rose-50 border border-rose-200 px-4 py-3 mb-6" data-testid="signup-error">
              <p className="font-body text-sm text-rose-700">{error}</p>
            </div>
          )}
          <div className="mb-5">
            <label className="block font-body text-sm font-medium text-zinc-700 mb-1.5">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-brand focus:ring-1 focus:ring-brand px-4 py-2.5 font-body text-sm text-zinc-900 outline-none transition-colors"
              placeholder="Your name"
              data-testid="signup-name-input"
            />
          </div>
          <div className="mb-5">
            <label className="block font-body text-sm font-medium text-zinc-700 mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-brand focus:ring-1 focus:ring-brand px-4 py-2.5 font-body text-sm text-zinc-900 outline-none transition-colors"
              placeholder="you@company.com"
              data-testid="signup-email-input"
            />
          </div>
          <div className="mb-6">
            <label className="block font-body text-sm font-medium text-zinc-700 mb-1.5">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-brand focus:ring-1 focus:ring-brand px-4 py-2.5 font-body text-sm text-zinc-900 outline-none transition-colors"
              placeholder="Minimum 6 characters"
              data-testid="signup-password-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand text-white hover:bg-brand-hover py-3 font-body font-medium text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="signup-submit-btn"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating account...</> : 'Create Account'}
          </button>
          <p className="mt-4 font-body text-xs text-zinc-400 text-center leading-relaxed">
            By creating an account, you agree to our Terms of Service. ContractEye does not provide legal advice.
          </p>
        </form>
        <p className="text-center mt-6 font-body text-sm text-zinc-500">
          Already have an account?{' '}
          <Link to="/login" className="text-brand font-medium hover:underline" data-testid="signup-login-link">Log in</Link>
        </p>
      </div>
    </div>
  );
}
